import React, { useState } from 'react';
import { useToast } from 'src/context/ToastProvider';
import { useMutation, useQueryClient } from 'react-query';
import { Button, FileInput, Modal, Spinner } from 'flowbite-react';
import { getPolicyForS3NonVideo, updateUserById } from 'src/apis/user';
import { useUserContext } from 'src/context/UserProvider';
import { handleS3Upload } from 'src/utils/imageUpload';
import { get } from 'lodash';

type Props = {
	isOpen: boolean;
	onClose: () => void;
};

const UploadProfileImage = ({ isOpen, onClose }: Props) => {
	const [file, setFile] = useState<File>();
	const toast = useToast();
	const { user } = useUserContext();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const queryClient = useQueryClient();

	const updateProfileByIdFn = useMutation(['updateUserById'], updateUserById, {
		onSuccess() {
			queryClient.refetchQueries('getUserProfile');
			queryClient.refetchQueries('verifyCookie');
			toast({
				title: 'Profile image updated successfully',
				type: 'success',
				duration: 3000,
			});
			setIsLoading(false);
			onClose();
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				type: 'error',
				title: errorMessage,
			});
			setIsLoading(false);
			onClose();
		},
	});

	const handleSubmitUser = async () => {
		if (file) {
			const fileSizeMB = file.size / (1024 * 1024); // Convert bytes to MB
			if (fileSizeMB > 5) {
				toast({
					type: 'error',
					title: 'File size exceeds the maximum limit of 5 MB',
					duration: 10000,
				});
				return;
			}
			setIsLoading(true);
			let imageUrl = '';
			if (file && user) {
				// fileName, fileLocation, mime
				const policy = await getPolicyForS3NonVideo(file.name, 'profileImages', file.type, true);
				const uploadedUrl = await handleS3Upload(policy, file);
				if (uploadedUrl) imageUrl = uploadedUrl;
				if (imageUrl) {
					updateProfileByIdFn.mutate({
						id: String(user?._id),
						data: {
							profile: {
								profileImageUrl: imageUrl,
							},
						},
					});
				}
			} else {
				setIsLoading(false);
			}
		}
	};

	return (
		<Modal dismissible={true} show={isOpen} onClose={onClose} size="lg" className="!h-full backdrop-blur-[4px]">
			<Modal.Header className="rounded-t-md border-b-0 dark:bg-[#1F2A37]">
				<div>
					<p className="font-inter text-[20px] font-[700] dark:text-white">Profile Image</p>
				</div>
			</Modal.Header>
			<Modal.Body className="dark:bg-[#1F2A37] !pt-[8px]">
				<div>
					<form className="text-white">
						<div className="mb-[8px]">
							<FileInput
								accept="image/png, image/jpeg"
								id="profileImage"
								name="profileImage"
								onChange={event => {
									if (event.target && event.target.files) {
										setFile && setFile(event.target.files[0]);
									}
								}}
							/>
						</div>
					</form>
				</div>
			</Modal.Body>
			<Modal.Footer className="rounded-b-md border-t-0 bg-[#f7f9fb] dark:bg-[#18222D]">
				<div className="flex w-full justify-end">
					<div className="">
						<Button color="purplePlaced" className="bg-[#7777E7]" disabled={isLoading} onClick={handleSubmitUser}>
							{isLoading && <Spinner className="mr-3" size={'sm'} light={true} />}
							Save
						</Button>
					</div>
				</div>
			</Modal.Footer>
		</Modal>
	);
};

export default UploadProfileImage;
